// Packages
import React, {
	useRef,
	useCallback,
	useState,
	useEffect,
	FC,
	ReactElement,
	useContext,
} from 'react';
import { cx } from '@emotion/css';
import { useDrag } from '@use-gesture/react';
import { useSprings, animated } from '@react-spring/web';

// Starterpack
import useThemeBlock from '@/hooks/useThemeBlock';
import { LocaleContext } from '@/contexts/locale-context';

// Helpers
import { getCurrentSlideIndex } from './helpers/get-current-slide-index';
import { getRealIndex } from './helpers/get-real-index';
import { needNavigation } from './helpers/need-navigation';
import { getFirstVisibleSlideIndex } from './helpers/get-first-visible-slide-index';
import { moveSlidesToFinalOffset } from './helpers/move-slides-to-final-offset';
import { getSlideRank } from './helpers/get-slide-rank';
import { getSlideConfigPositionIndex } from './helpers/get-slide-config-position-index';
import { getSlideOffset } from './helpers/get-slide-offset';
import { getWidthToOffset } from './helpers/get-width-to-offset';
import { getNextSlideIndex } from './helpers/get-next-slide-index';
import { getIsNextSlide } from './helpers/get-is-next-slide';
import { getPreviousSlideIndex } from './helpers/get-previous-slide-index';
import { getIsPreviousSlide } from './helpers/get-is-previous-slide';

// Contexts
import { SlideContext, SliderContext } from './context';

// Styles
import { getStyles } from './styles.css';
import block from './block.json';

// TODO :: Add a readme ?

/**
 * TYPES
 */
export type SliderProps = {
	slides: Array<any>;
	className?: string;
	loop?: boolean;
	onAfterInit?: Function;
	transition?: {
		tension?: number;
		friction?: number;
		mass?: number;
	};
	navigation?: {
		icon?: ReactElement;
	};
	pagination?: {
		type?: 'bullets' | 'fraction';
	};
	hasStagger?: boolean;
	centeredSlides?: boolean;
} & ThemeProps;

/**
 * COMPONENT
 */
export const Slider: FC<SliderProps> & BlockConfigs = ({
	slides,
	className,
	loop = false,
	transition,
	navigation,
	pagination,
	hasStagger = false,
	centeredSlides = false,
	onAfterInit,
	theme = {},
}) => {
	const [items, setItems] = useState(slides);
	const [currentRealIndex, setCurrentRealIndex] = useState(0);
	const [widths, setWidths] = useState(null);
	const [reversedWidths, setReversedWidths] = useState(null);
	const [totalWidths, setTotalWidths] = useState(0);
	const [height, setHeight] = useState(null);
	const [bounds, setBounds] = useState(0);
	const [isReady, setIsReady] = useState(false);
	const [isAtBeginning, setIsAtBeginning] = useState(loop ? false : true);
	const [isAtEnd, setIsAtEnd] = useState(false);
	const [isDesktop, setIsDesktop] = useState(false);

	const wrapperRef = useRef(null);
	const prev = useRef([0, 1]);
	const previousDirection = useRef(0);
	const dragOffset = useRef(0);

	const { __t } = useContext(LocaleContext);

	// Styles
	const { variant, blockTheme } = useThemeBlock(block.slug, theme);
	const rootClass = cx('supt-slider', className, getStyles(blockTheme), {
		'is-ready': isReady,
		'no-need-navigation': !needNavigation(loop, dragOffset.current, bounds),
	});

	// Init react spring for slides transitions
	const [springs, api] = useSprings(items.length, () => ({
		x: 0,
	}));

	/**
	 * Clone slides if slider length is below 5 (only in loop mode, to avoid bugs)
	 */
	useEffect(() => {
		setIsReady(false); // Reset ready to false to avoid having a transition when the new slides are positionned

		if (loop && items.length < 5) {
			let clonedItems = [];

			while (clonedItems.length < 5) {
				clonedItems.push(...slides);
			}
			setItems(clonedItems);
		} else {
			setItems(slides);
		}
	}, [slides]);

	/**
	 * Set slides width + slider height + slider bounds
	 * (Triggered on init + on resize)
	 */
	useEffect(() => {
		let resizeTimer;
		const handleWindowResize = () => {
			clearTimeout(resizeTimer);

			resizeTimer = setTimeout(() => {
				if (!wrapperRef.current) return; // Fix for dom not ready yet on WP Editor side

				// Get slides width
				const childWidths = [...wrapperRef.current.children].map(
					(child) => child.offsetWidth
				);
				setWidths(childWidths);

				// Reversed slides width
				const reversedChildWidths = [...childWidths].reverse();
				setReversedWidths(reversedChildWidths);

				// Get total slides width to get the slider width
				const totalChildWidths = childWidths.reduce(
					(acc, w) => acc + w,
					0
				);
				setTotalWidths(totalChildWidths + 1);

				// Get slider height (depending on the slides biggest height)
				const childHeights = [...wrapperRef.current.children].map(
					(child) => child.offsetHeight
				);
				const childHeight = Math.max(...childHeights);
				setHeight(childHeight);

				// Set bounds (only if NOT loop) => gets the maximum pixel user can drag on right
				if (!loop) {
					setBounds(
						totalChildWidths - wrapperRef.current.offsetWidth
					);
				}
			}, 200);
		};

		handleWindowResize(); // Trigger on load

		window.addEventListener('resize', handleWindowResize);

		return () => {
			window.removeEventListener('resize', handleWindowResize);
		};
	}, [slides]);

	/**
	 * Get if current device is a desktop or not
	 */
	useEffect(() => {
		// MQ for devices that have a mouse or trackpad
		const desktopMQ = window.matchMedia(
			'(hover: hover) and (pointer: fine)'
		);

		if (desktopMQ.matches) {
			setIsDesktop(true);
		} else {
			setIsDesktop(false);
		}
	}, []);

	/**
	 * Initialize the slides positions
	 */
	useEffect(() => {
		if (widths && totalWidths && reversedWidths) {
			// Reset slides positions
			const newWidthToOffset = getWidthToOffset(widths, currentRealIndex);
			dragOffset.current = newWidthToOffset;

			moveSlides({
				x: newWidthToOffset,
				dx: 1,
				immediate: isReady ? null : true,
			});

			if (!isReady) {
				// Slider is now ready
				setIsReady(true);

				if (onAfterInit) onAfterInit();
			}
		}
	}, [widths, totalWidths, reversedWidths]);

	/**
	 * Handle callbacks when user drags slider
	 */
	const bind = useDrag(
		({
			offset: [x],
			direction: [dx],
			movement: [movementX],
			down,
			event,
			last,
		}) => {
			event.preventDefault();

			// On Drag End
			if (last) {
				// Bail early if the slider isn't big enough so no need to translate the slides
				if (!needNavigation(loop, dragOffset.current, bounds)) return;

				dragOffset.current = -x;
				moveSlides({ x: -x, dx: -dx, onEnd: true, movementX });
			} else {
				// Bail early if the slider isn't big enough so no need to translate the slides
				if (!needNavigation(loop, dragOffset.current, bounds)) return;

				if (dx && down) {
					dragOffset.current = -x;
					moveSlides({ x: -x, dx: -dx, onEnd: false, movementX });
				}
			}
		},
		{
			// Start drag at the last position
			from: () => [-dragOffset.current, 0],
			// Add bounds if it's NOT a loop (=> we can drag only between 0 and the slider width)
			bounds: () => (loop ? {} : { left: -1 * bounds, right: 0 }),
			// Prevent clicking on a link while dragging
			preventDefault: true,
			// filterTaps: true, // COMMENT => on mobile we couldn't click on a button inside the slider
			preventScroll: true,
		}
	);

	/**
	 * Get position of a specific slide
	 */
	const getPosIndex = useCallback(
		(i, currentSlideIndex, firstVisibleSlideIndex) =>
			getRealIndex(
				i - currentSlideIndex + firstVisibleSlideIndex /*+1*/,
				items.length
			),
		[items]
	);

	/**
	 * Move slides depending on the current drag offset
	 */
	const moveSlides = useCallback(
		({ x, dx, onEnd = false, movementX = null, immediate = null }) => {
			// Get real index of current slide (between 0 and items.length)
			let currentSlideIndex = getCurrentSlideIndex({
				x,
				widths,
				reversedWidths,
				totalWidths,
				items,
			});

			// console.log('currentSlideIndex :: ', currentSlideIndex);

			// Get first visible slide index
			const firstVisibleSlideIndex = getFirstVisibleSlideIndex({
				loop,
				currentSlideIndex,
			});

			// If user is not dragging anymore
			if (onEnd) {
				let slideIndex = currentSlideIndex;

				// If mobile and user didn't drag much => force going to the previous or next slide
				if (
					!isDesktop &&
					movementX &&
					Math.abs(movementX) < widths[currentSlideIndex] / 2
				) {
					if (movementX < 0) {
						// => Move to next slide
						slideIndex = loop
							? currentRealIndex + 1
							: getNextSlideIndex(currentRealIndex, items);
					} else if (movementX > 0) {
						// => Move to previous slide
						slideIndex = loop
							? currentRealIndex - 1
							: getPreviousSlideIndex(currentRealIndex, items);
					}

					// Set new currentSlideIndex for loop animations
					if (loop) {
						currentSlideIndex = slideIndex;
					}
				}

				// Force to go to the begginning of the slide

				// Set the slides to their final positions
				moveSlidesToFinalOffset({
					loop,
					currentSlideIndex: slideIndex,
					x,
					dragOffset,
					widths,
					reversedWidths,
					items,
					bounds,
				});

				// Set current real index
				setCurrentRealIndex(getRealIndex(slideIndex, items.length));
			}
			// console.log('-----');

			api.start((i) => {
				// Get position index of the slide (=> position is between 0 and items.length)
				const positionIndex = getPosIndex(
					i,
					currentSlideIndex,
					firstVisibleSlideIndex
				);
				// console.log('positionIndex :: ', i, positionIndex);

				// Get previous position index of the slide (=> position is between 0 and items.length)
				// This will be needed to know if the slide will transform smoothly to its new position, or immediatly without any transition
				// (in the case it was at 0 and needs now to be at 5 so move from the left to the right instantly)
				const prevPositionIndex = getPosIndex(
					i,
					prev.current[0],
					prev.current[1]
				);

				// Get rank of current slide
				const rank = getSlideRank({
					currentSlideIndex,
					firstVisibleSlideIndex,
					x,
					positionIndex,
					items,
				});

				let configPos;
				if (hasStagger) {
					// Get position index of the slide to use for the transition configs
					// (only if it hasStagger, meaning each slide will have a different transition config)
					configPos = getSlideConfigPositionIndex({
						loop,
						currentSlideIndex,
						positionIndex,
						dx,
						previousDirection,
						items,
					});

					// console.log('configPos :: ', i, configPos);
				}

				// Get slide offset
				const slideOffset = getSlideOffset({
					rank,
					i,
					widths,
					reversedWidths,
					centeredSlides,
				});

				// Get slide translate X depending of slideOffset + current x or dragOffset
				let slideTranslateX = onEnd
					? (-dragOffset.current % totalWidths) + slideOffset
					: (-x % totalWidths) + slideOffset;

				return {
					x: slideTranslateX,
					// Move immediatly (and not smoothly) if the slide position go from 0 to items.length for example
					immediate: immediate
						? immediate
						: dx < 0
						? prevPositionIndex > positionIndex
						: prevPositionIndex < positionIndex,
					// The further the slide is from the current slide, the slower it transition will be
					config: {
						tension: hasStagger
							? (1 + items.length - configPos) *
							  (transition?.tension ?? 230)
							: transition?.tension ?? 230,
						friction: transition?.friction ?? 120,
						mass: transition?.mass ?? 1,
					},
				};
			});

			// Save current values to use next time
			prev.current = [currentSlideIndex, firstVisibleSlideIndex];
			if (dx !== 0) previousDirection.current = dx;

			// Check if we're at the beginning or the end of the slider in order to disable prev/next buttons if needed
			// (Only if not in loop mode)
			if (!loop) {
				if (dragOffset.current <= 0) {
					setIsAtBeginning(true);
					setIsAtEnd(false);
				} else if (dragOffset.current >= bounds) {
					setIsAtEnd(true);
					setIsAtBeginning(false);
				} else {
					if (isAtBeginning) setIsAtBeginning(false);
					if (isAtEnd) setIsAtEnd(false);
				}
			}
		},
		[
			getPosIndex,
			widths,
			totalWidths,
			api,
			items.length,
			setCurrentRealIndex,
			dragOffset,
			bounds,
			prev.current,
		]
	);

	/**
	 * Handle click on Previous button navigation
	 */
	const handlePrevClick = () => {
		if (isAtBeginning) return; // don't go prev if we are already at the beginning of the slider

		// Get new current slide index to know the width to move from
		const currentIndex = getPreviousSlideIndex(currentRealIndex, items);

		if (!loop && dragOffset.current === bounds) {
			// If we are currently to the maximum bounds on the right of the slider, move to the previous slide by calculating the exact width to move from
			// (and not move from the whole width of the slide)
			dragOffset.current = getWidthToOffset(widths, currentIndex);
		} else {
			// Move from the new current slide width
			dragOffset.current -= widths[currentIndex];
		}

		// Move slides to the right
		moveSlides({ x: dragOffset.current, dx: -1 });

		setCurrentRealIndex(currentIndex);
	};

	/**
	 * Handle click on Next button navigation
	 */
	const handleNextClick = () => {
		if (isAtEnd) return; // don't go next if we are already at the end of the slider

		// Move from the current slide width
		dragOffset.current += widths[currentRealIndex];

		// Move slides to the left
		moveSlides({
			x: loop ? dragOffset.current : Math.min(dragOffset.current, bounds),
			dx: 1,
		}); // Go maximum to the bounds if it's not in loop mode

		// Get new current slide index
		const currentIndex = getNextSlideIndex(currentRealIndex, items);

		setCurrentRealIndex(currentIndex);
	};

	return (
		<SliderContext.Provider
			value={{
				wrapperRef,
				isReady,
				currentRealIndex,
			}}
		>
			<div
				className={rootClass}
				data-block={block.slug}
				data-variant={variant}
			>
				<div className="supt-slider__controls">
					<div className="supt-slider__navigation">
						<button
							onClick={handlePrevClick}
							aria-label={__t(
								'slider-navigation-prev-label',
								'Previous slide'
							)}
							className="supt-slider__button supt-slider__button--prev"
							disabled={isAtBeginning}
						>
							<span>
								{navigation?.icon ?? (
									<svg
										viewBox="0 0 21 16"
										xmlns="http://www.w3.org/2000/svg"
									>
										<g
											stroke="#000"
											strokeWidth="1"
											fill="none"
											fillRule="evenodd"
										>
											<path d="M0 8h20M13.5 1l7 7-7 7" />
										</g>
									</svg>
								)}
							</span>
						</button>

						<button
							aria-label={__t(
								'slider-navigation-next-label',
								'Next slide'
							)}
							className="supt-slider__button supt-slider__button--next"
							onClick={handleNextClick}
							disabled={isAtEnd}
						>
							<span>
								{navigation?.icon ?? (
									<svg
										viewBox="0 0 21 16"
										xmlns="http://www.w3.org/2000/svg"
									>
										<g
											stroke="#000"
											strokeWidth="1"
											fill="none"
											fillRule="evenodd"
										>
											<path d="M0 8h20M13.5 1l7 7-7 7" />
										</g>
									</svg>
								)}
							</span>
						</button>
					</div>
					{pagination ? (
						<div className="supt-slider__pagination">
							{pagination.type === 'fraction' ? (
								// TODO :: Clickable pagination
								<span className="supt-slider__pagination__fraction">
									{String(
										(currentRealIndex % slides.length) + 1
									).padStart(2, '0')}{' '}
									/ {String(slides.length).padStart(2, '0')}
								</span>
							) : (
								slides.map((slide, i) => (
									<span
										key={i}
										className={cx(
											'supt-slider__pagination__bullet',
											{
												'is-active':
													i ===
													currentRealIndex %
														slides.length,
											}
										)}
									/>
								))
							)}
						</div>
					) : null}
				</div>

				<div className="supt-slider__container">
					<div
						ref={wrapperRef}
						{...bind()}
						className="supt-slider__wrapper"
						style={{ height: `${height}px` }}
					>
						{springs.map(({ x }, i) => (
							<SlideContext.Provider
								key={i}
								value={{
									isActive:
										currentRealIndex === i % items.length,
								}}
							>
								<animated.div
									style={{ x }}
									children={items[i]}
									className={cx('supt-slider__slide', {
										'is-active':
											currentRealIndex ===
											i % items.length,
										'is-prev':
											loop &&
											getIsPreviousSlide({
												i,
												currentRealIndex,
												items,
											}),
										'is-next':
											loop &&
											getIsNextSlide({
												i,
												currentRealIndex,
												items,
											}),
									})}
								/>
							</SlideContext.Provider>
						))}
					</div>
				</div>
			</div>
		</SliderContext.Provider>
	);
};

Slider.slug = block.slug;
Slider.title = block.title;
